/**
 * The Onlymind logo system (glossary: Brand & styling).
 *
 *   <Wordmark />   ONLYMIND, for headers
 *   <RabbitHead /> the compact symbol: icons, theme cards
 *   <Lockup />     head + wordmark, spaced as the brand book's safe-zone page
 *   <Mascot />     the full-body rabbit, for personality (sign-in, start-up)
 *
 * The brand themes show the glossy artwork; the alternative themes show the
 * flat rabbit coloured from that theme. Shapes and proportions never change.
 */
import type { CSSProperties } from 'react';
import { isLightTheme, useTheme, type Theme } from '../theme/themes';
import { BODY_FLAT, HEAD_FLAT, WORDMARK, type FlatArt } from './artwork';
import './brand.css';

/** How the flat rabbit is coloured on each alternative theme. */
const FLAT_COLOURS: Partial<Record<Theme, Record<string, string>>> = {
  'onlyfans-dark': { '--m-fill': '#1a1a1a', '--m-eye': '#ffffff', '--m-pupil': '#1a1a1a', '--m-line': '#00aff0', '--m-edge': '1.5px' },
  'onlyfans-light': { '--m-fill': '#ffffff', '--m-eye': '#ffffff', '--m-pupil': '#15171b', '--m-line': '#00aff0', '--m-edge': '1.25px' },
  aurora: { '--m-fill': '#0f1d2a', '--m-eye': '#ffffff', '--m-pupil': '#0f1d2a', '--m-line': '#2fc4f2', '--m-edge': '1.5px' },
};

const isBrandTheme = (theme: Theme) => theme === 'onlymind' || theme === 'onlymind-light';

/** The glossy artwork: dark on Onlymind, lavender on Onlymind Light. */
const glossy = (part: 'head' | 'body', theme: Theme) => `/brand/mascot-${part}-${isLightTheme(theme) ? 'light' : 'dark'}.webp`;

/** Head and body art keep the brand files' proportions (width / height). */
export const HEAD_RATIO = 640 / 401;
export const BODY_RATIO = 800 / 675;
const WORDMARK_RATIO = (() => {
  const [, , w, h] = WORDMARK.viewBox.split(' ').map(Number);
  return w / h;
})();

function FlatRabbit({ art, theme, style }: { art: FlatArt; theme: Theme; style?: CSSProperties }) {
  return (
    <svg className="om-flat" viewBox={art.viewBox} style={{ ...FLAT_COLOURS[theme], ...style } as CSSProperties} aria-hidden="true">
      <path className="om-flat-fill" d={art.fill} fillRule="evenodd" />
      <path className="om-flat-eye" d={art.eye} fillRule="evenodd" />
      <path className="om-flat-pupil" d={art.pupil} fillRule="evenodd" />
      <path className="om-flat-line" d={art.line} fillRule="evenodd" />
    </svg>
  );
}

interface ArtProps {
  /** Width in px; the height follows the artwork. */
  width: number;
  /** Draw as this theme instead of the one in use (the theme picker's cards). */
  theme?: Theme;
  className?: string;
  /** Screen-reader name; omit when the art is decorative. */
  label?: string;
}

function Art({ part, width, theme: forced, className, label }: ArtProps & { part: 'head' | 'body' }) {
  const [current] = useTheme();
  const theme = forced ?? current;
  const ratio = part === 'head' ? HEAD_RATIO : BODY_RATIO;
  const height = Math.round(width / ratio);
  const cls = `om-art${className ? ` ${className}` : ''}`;
  const box = { width, aspectRatio: String(ratio) };
  const a11y = label ? { role: 'img', 'aria-label': label } : { 'aria-hidden': true };
  if (isBrandTheme(theme)) {
    return (
      <span className={cls} style={box} {...a11y}>
        <img src={glossy(part, theme)} alt="" width={width} height={height} draggable={false} />
      </span>
    );
  }
  return (
    <span className={cls} style={box} {...a11y}>
      <FlatRabbit art={part === 'head' ? HEAD_FLAT : BODY_FLAT} theme={theme} />
    </span>
  );
}

export function RabbitHead(props: ArtProps) {
  return <Art part="head" {...props} />;
}

export function Mascot(props: ArtProps) {
  return <Art part="body" {...props} />;
}

/** ONLYMIND in the theme's text colour. An empty label makes it decorative. */
export function Wordmark({ height, className, label = 'Onlymind' }: { height: number; className?: string; label?: string }) {
  return (
    <svg
      className={`om-wordmark${className ? ` ${className}` : ''}`}
      viewBox={WORDMARK.viewBox}
      width={Math.round(height * WORDMARK_RATIO)}
      height={height}
      {...(label ? { role: 'img', 'aria-label': label } : { 'aria-hidden': true })}
    >
      <path fill="currentColor" d={WORDMARK.d} />
    </svg>
  );
}

/**
 * Rabbit head + wordmark. Spaced as the brand book's safe zone: at a 458-wide
 * wordmark the head is 166 wide with a 32 gap, so both scale off the wordmark.
 */
export function Lockup({ height, className, theme }: { height: number; className?: string; theme?: Theme }) {
  const wordWidth = height * WORDMARK_RATIO;
  const headWidth = Math.round((wordWidth * 166) / 458);
  const gap = Math.round((wordWidth * 32) / 458);
  return (
    <span className={`om-lockup${className ? ` ${className}` : ''}`} style={{ gap }}>
      <RabbitHead width={headWidth} theme={theme} />
      <Wordmark height={height} />
    </span>
  );
}
